import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  MessageCircle, Phone, Send, RefreshCw, Settings, Users,
  Loader2, CheckCircle, XCircle, Clock, ChevronRight, Copy, Check
} from 'lucide-react';
import Navbar from '../../components/layout/Navbar';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function WhatsAppAdminPage() {
  const [status, setStatus] = useState(null);
  const [conversations, setConversations] = useState({});
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [conversationHistory, setConversationHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [copied, setCopied] = useState(false);
  
  // Send message form
  const [phoneNumber, setPhoneNumber] = useState('');
  const [messageText, setMessageText] = useState('');
  
  const token = localStorage.getItem('upim_token');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      const [statusRes, convsRes] = await Promise.all([
        axios.get(`${API_URL}/api/whatsapp/status`, { headers }),
        axios.get(`${API_URL}/api/whatsapp/conversations`, { headers })
      ]);
      
      setStatus(statusRes.data);
      setConversations(convsRes.data.conversations || {});
    } catch (err) {
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadConversationHistory = async (phone) => {
    try {
      const res = await axios.get(`${API_URL}/api/whatsapp/conversations/${phone}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConversationHistory(res.data.messages || []);
      setSelectedConversation(phone);
    } catch (err) {
      console.error('Error loading conversation:', err);
    }
  };

  const sendMessage = async () => {
    if (!phoneNumber || !messageText) {
      alert('Ingresa número y mensaje');
      return;
    }
    
    setSendingMessage(true);
    try {
      await axios.post(`${API_URL}/api/whatsapp/send`, {
        phone_number: phoneNumber,
        message: messageText
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      alert('Mensaje enviado');
      setMessageText('');
      loadData();
    } catch (err) {
      alert('Error al enviar: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSendingMessage(false);
    }
  };

  const copyWebhookUrl = () => {
    navigator.clipboard.writeText(`${API_URL}/api/whatsapp/webhook`);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
        </div>
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gray-50 py-8 px-4">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                <MessageCircle className="w-8 h-8 text-green-600" />
                WhatsApp Business
              </h1>
              <p className="text-gray-500 mt-1">Gestión del agente de WhatsApp con IA</p>
            </div>
            <button
              onClick={loadData}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              <RefreshCw className="w-4 h-4" />
              Actualizar
            </button>
          </div>

          {/* Status Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-white rounded-xl p-4 shadow-sm">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${status?.configured ? 'bg-green-100' : 'bg-red-100'}`}>
                  {status?.configured ? (
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  ) : (
                    <XCircle className="w-5 h-5 text-red-600" />
                  )}
                </div>
                <div>
                  <p className="text-lg font-bold text-gray-900">
                    {status?.configured ? 'Activo' : 'Inactivo'}
                  </p>
                  <p className="text-sm text-gray-500">Estado</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-xl p-4 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Phone className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-lg font-bold text-gray-900 truncate max-w-[120px]">
                    {status?.phone_number_id?.slice(-6) || '-'}
                  </p>
                  <p className="text-sm text-gray-500">Phone ID</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-xl p-4 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Users className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-lg font-bold text-gray-900">
                    {status?.active_conversations || 0}
                  </p>
                  <p className="text-sm text-gray-500">Conversaciones</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-xl p-4 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                  <Settings className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-lg font-bold text-gray-900 truncate max-w-[120px]">
                    {status?.verify_token?.slice(0, 10)}...
                  </p>
                  <p className="text-sm text-gray-500">Verify Token</p>
                </div>
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Webhook Configuration */}
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Settings className="w-5 h-5 text-indigo-600" />
                Configuración Webhook
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">URL del Webhook</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      readOnly
                      value={`${API_URL}/api/whatsapp/webhook`}
                      className="flex-1 px-3 py-2 bg-gray-100 rounded-lg text-sm text-gray-600"
                    />
                    <button
                      onClick={copyWebhookUrl}
                      className="p-2 bg-gray-100 rounded-lg hover:bg-gray-200"
                    >
                      {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Token de Verificación</label>
                  <input
                    type="text"
                    readOnly
                    value={status?.verify_token || ''}
                    className="w-full px-3 py-2 bg-gray-100 rounded-lg text-sm text-gray-600"
                  />
                </div>
                
                <div className="p-4 bg-blue-50 rounded-lg">
                  <h4 className="font-medium text-blue-900 mb-2">Pasos para configurar:</h4>
                  <ol className="text-sm text-blue-700 space-y-1 list-decimal list-inside">
                    <li>Ve a developers.facebook.com</li>
                    <li>Selecciona tu app → WhatsApp → Configuración</li>
                    <li>En "Webhook", pega la URL de arriba</li>
                    <li>Pega el Token de Verificación</li>
                    <li>Suscríbete a "messages"</li>
                  </ol>
                </div>
              </div>
            </div>

            {/* Send Test Message */}
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Send className="w-5 h-5 text-green-600" />
                Enviar Mensaje de Prueba
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Número de WhatsApp
                  </label>
                  <input
                    type="tel"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    placeholder="521234567890 (con código país)"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Mensaje
                  </label>
                  <textarea
                    value={messageText}
                    onChange={(e) => setMessageText(e.target.value)}
                    rows={3}
                    placeholder="Escribe tu mensaje..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                </div>
                
                <button
                  onClick={sendMessage}
                  disabled={sendingMessage || !phoneNumber || !messageText}
                  className="w-full flex items-center justify-center gap-2 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                >
                  {sendingMessage ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <Send className="w-5 h-5" />
                  )}
                  Enviar Mensaje
                </button>
              </div>
            </div>
          </div>

          {/* Conversations */}
          <div className="mt-6 bg-white rounded-xl shadow-sm overflow-hidden">
            <div className="p-6 border-b">
              <h3 className="font-semibold text-gray-900">Conversaciones Activas</h3>
            </div>
            
            <div className="grid md:grid-cols-3">
              {/* Conversation List */}
              <div className="border-r">
                {Object.keys(conversations).length === 0 ? (
                  <div className="p-8 text-center text-gray-500">
                    <MessageCircle className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p>Sin conversaciones aún</p>
                  </div>
                ) : (
                  <div className="divide-y max-h-[400px] overflow-y-auto">
                    {Object.entries(conversations).map(([phone, data]) => (
                      <button
                        key={phone}
                        onClick={() => loadConversationHistory(phone)}
                        className={`w-full p-4 text-left hover:bg-gray-50 ${
                          selectedConversation === phone ? 'bg-green-50' : ''
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium text-gray-900">{phone}</p>
                            <p className="text-sm text-gray-500">
                              {data.message_count} mensajes
                            </p>
                          </div>
                          <ChevronRight className="w-4 h-4 text-gray-400" />
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Conversation Detail */}
              <div className="md:col-span-2 max-h-[400px] overflow-y-auto">
                {selectedConversation ? (
                  <div className="p-4 space-y-3">
                    {conversationHistory.map((msg, i) => (
                      <div
                        key={i}
                        className={`flex ${msg.role === 'user' ? 'justify-start' : 'justify-end'}`}
                      >
                        <div className={`max-w-[80%] p-3 rounded-2xl ${
                          msg.role === 'user'
                            ? 'bg-gray-100 text-gray-800'
                            : 'bg-green-600 text-white'
                        }`}>
                          <p className="text-sm">{msg.content}</p>
                          <p className={`text-xs mt-1 ${
                            msg.role === 'user' ? 'text-gray-500' : 'text-green-200'
                          }`}>
                            {new Date(msg.timestamp).toLocaleTimeString('es-MX')}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="p-12 text-center text-gray-500">
                    <MessageCircle className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                    <p>Selecciona una conversación</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Info Banner */}
          <div className="mt-8 bg-gradient-to-r from-green-600 to-green-700 rounded-2xl p-6 text-white">
            <div className="flex items-start gap-4">
              <MessageCircle className="w-10 h-10 flex-shrink-0" />
              <div>
                <h3 className="text-xl font-bold mb-2">WhatsApp Business API</h3>
                <p className="text-green-100 text-sm leading-relaxed">
                  El agente de WhatsApp responde automáticamente a los usuarios con IA, 
                  proporcionando información sobre UPIM, membresías, capacitaciones y más. 
                  Los usuarios pueden contactar al número de WhatsApp de UPIM y recibir 
                  atención 24/7.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
